import { useId, ComponentType } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { Description, inputStyles, Label } from 'src/components/ui/Field';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input/min';
import 'react-phone-number-input/style.css';
import {
  Select as AriaSelect,
  Button,
  ListBox,
  ListBoxItem,
  SelectValue,
} from 'react-aria-components';
import { dropdownItemStyles } from 'src/components/ui/Select';
import { Popover } from 'src/components/ui/Popover';
import ExpandIcon from 'src/assets/icons/move-expand-vertical.svg';

import type { ColorVariant, Size } from 'src/types/common';

type CountrySelectOption = {
  value: string | undefined;
  label: string;
  divider?: boolean;
};

interface PhoneCountrySelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: CountrySelectOption[];
  iconComponent: ComponentType<{ country?: string; label: string }>;
  disabled?: boolean;
  readOnly?: boolean;
  tabIndex?: number | string;
}

const PhoneCountrySelect = ({
  value,
  onChange,
  options,
  iconComponent: FlagIcon,
  disabled,
  readOnly,
  tabIndex,
}: PhoneCountrySelectProps) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const selectableOptions = options.filter((opt) => !opt.divider);

  return (
    <AriaSelect
      selectedKey={value ?? ''}
      onSelectionChange={(key) => onChange(key as string)}
      isDisabled={disabled || readOnly}
      aria-label="Select country"
      className="h-full"
    >
      <Button
        excludeFromTabOrder={Number(tabIndex) === -1}
        className="flex items-center gap-1 h-full pl-2 pr-1 mr-1 rounded-l bg-surface-ghost text-surface-ghost-75 hover:bg-surface-ghost-hover border-r border-surface-10 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-secondary"
      >
        <FlagIcon country={value} label={selectedOption?.label ?? ''} />
        <SelectValue className="sr-only" />
        <ExpandIcon aria-hidden className="text-xs opacity-60" />
      </Button>
      <Popover className="min-w-[200px]">
        <ListBox
          items={selectableOptions}
          className="bg-surface-alt outline-none p-1.5 max-h-64 overflow-auto [clip-path:inset(0_0_0_0_round_.75rem)]"
        >
          {(item) => (
            <ListBoxItem
              key={item.value ?? ''}
              id={item.value ?? ''}
              textValue={item.label}
              className={dropdownItemStyles}
            >
              <FlagIcon country={item.value} label={item.label} />
              <span className="flex-1 text-sm font-semibold">{item.label}</span>
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
};

interface FormPhoneFieldProps {
  name: string;
  label?: string;
  description?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  variant?: ColorVariant;
  size?: Size;
  className?: string;
}

const FormPhoneField = ({
  name,
  label,
  size = 'medium',
  variant = 'surface',
  isRequired = false,
  description,
  className,
  ...rest
}: FormPhoneFieldProps) => {
  const { control } = useFormContext();
  const phoneInputId = useId();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { invalid, error } }) => {
        const e164Value = toE164Phone(field.value);
        const descriptionId = description ? `${name}-description` : undefined;
        const errorId = invalid ? `${name}-error` : undefined;
        const describedBy =
          [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

        return (
          <div
            className={twMerge('group flex flex-col gap-1', className)}
            data-rhf-name={name}
          >
            {label && (
              <Label htmlFor={phoneInputId} isRequired={isRequired}>
                {label}
              </Label>
            )}
            <PhoneInput
              {...rest}
              id={phoneInputId}
              value={e164Value}
              initialValueFormat="national"
              onChange={(v) => field.onChange(v || '')}
              onBlur={field.onBlur}
              countries={['US']}
              // addInternationalOption={false}
              defaultCountry="US"
              placeholder="(234) 567-8999"
              autoComplete="tel"
              required={isRequired}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              countrySelectComponent={PhoneCountrySelect}
              className={inputStyles({
                ...rest,
                size,
                variant,
                isInvalid: invalid,
              })}
            />
            {description && (
              <Description id={descriptionId}>{description}</Description>
            )}
            {invalid && (
              <span
                id={errorId}
                role="alert"
                className="text-xs text-red font-medium"
              >
                {error?.message}
              </span>
            )}
          </div>
        );
      }}
    />
  );
};

export const toE164Phone = (phone?: string): string | undefined => {
  if (!phone) return phone;
  try {
    return parsePhoneNumber(phone, 'US')?.number ?? phone;
  } catch {
    return phone;
  }
};

export default FormPhoneField;
